import { useQuery, useMutation, gql } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';
import { GET_TAGS as GET_POSSIBLE_TAGS } from '../../../queries/tags';
import { TagsPicker } from '../../../components/Protected/TagsPicker';

const GET_SINGLE_IMAGE = gql`
  query Image($imageId: Int!) {
    image(id: $imageId) {
      id
      url
      tags {
        id
        groupId
        name
      }
      subjects {
        id
        user {
          name
        }
      }
    }
  }
`;

const GET_MEMBERS_I_CAN_TAG = gql`
  query MembersICanTag {
    membersICanTag {
      id
      user {
        name
      }
    }
  }
`;

const UPDATE_IMAGE_TAGS = gql`
  mutation UpdateImageTags($imageId: Int!, $tagIds: [Int!]!) {
    updateImageTags(imageId: $imageId, tagIds: $tagIds) {
      id
      name
    }
  }
`;

const UPDATE_MEMBER_IMAGES = gql`
  mutation UpdateMemberImages($imageId: Int!, $membershipIds: [Int!]!) {
    updateMemberImages(imageId: $imageId, membershipIds: $membershipIds) {
      id
      subjects {
        id
        user {
          name
        }
      }
    }
  }
`;

function TagImage() {
  const { imageId } = useParams<{ imageId: string }>();
  const idNum = Number(imageId);
  const navigate = useNavigate();

  const {
    data: imageData,
    loading: imageLoading,
    refetch: refetchImage,
  } = useQuery(GET_SINGLE_IMAGE, {
    variables: { imageId: idNum },
  });

  const {
    data: allTagsData,
    loading: tagsLoading,
    error: tagsError,
  } = useQuery(GET_POSSIBLE_TAGS, {
    variables: {
      showPeopleTags: false,
      showCourseTags: true,
    },
  });

  const {
    data: membersData,
    loading: membersLoading,
    error: membersError,
  } = useQuery(GET_MEMBERS_I_CAN_TAG);

  const [updateImageTags] = useMutation(UPDATE_IMAGE_TAGS);
  const [updateMemberImages] = useMutation(UPDATE_MEMBER_IMAGES);

  if (imageLoading) return <div style={{ padding: '20px' }}>Loading Image...</div>;
  if (!imageData?.image) return <div style={{ padding: '20px' }}>Image not found</div>;

  const currentSubjects = imageData.image.subjects.map((s: any) => ({
    id: s.id,
    name: s.user?.name || 'Unknown User',
  }));

  const availableMembers = {
    tags: membersData?.membersICanTag?.map((m: any) => ({
      id: m.id,
      name: m.user?.name || 'Unknown Member',
    })) || [],
  };

  const scopes = imageData.image.tags.filter((t: any) => t.groupId === 2);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px' }}>
      <button
        onClick={() => navigate(-1)}
        style={{
          marginBottom: '20px',
          padding: '8px 16px',
          cursor: 'pointer',
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        ← Back
      </button>

      <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start' }}>
        {/* Left: Image Container */}
        <div
          style={{
            flex: '2',
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            overflow: 'hidden',
            padding: '10px',
          }}
        >
          <img
            src={imageData.image.url}
            alt="Target"
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              borderRadius: '4px',
            }}
          />
        </div>

        {/* Right: Management Controls */}
        <div style={{ flex: '1', minWidth: '300px' }}>
          <h3 style={{ marginTop: 0 }}>Image Management</h3>

          <div style={{ marginBottom: '25px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '6px', border: '1px solid #eee' }}>
            <p style={{ fontWeight: 'bold', margin: '0 0 10px 0', fontSize: '0.9rem', color: '#555' }}>Communities with visibility</p>
            {scopes.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#333' }}>
                {scopes.map((scope: any) => (
                  <li key={scope.id} style={{ marginBottom: '4px' }}>{scope.name}</li>
                ))}
              </ul>
            ) : (
              <span style={{ fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>No scopes selected</span>
            )}
          </div>

          <div style={{ marginBottom: '30px' }}>
            <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>General Tags</p>
            <TagsPicker
              tags={imageData.image.tags}
              refetchParent={refetchImage}
              loading={tagsLoading}
              error={tagsError}
              data={allTagsData}
              updateTags={updateImageTags}
              payload={{ imageId: idNum }}
            />
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '30px 0' }} />

          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Associated Members</p>
            <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '15px' }}>
              Tag people that appear in this image.
            </p>

            <TagsPicker
              tags={currentSubjects}
              refetchParent={refetchImage}
              loading={membersLoading}
              error={membersError}
              data={availableMembers}
              updateTags={(options: any) => {
                const ids = options.variables?.tagIds || [];
                return updateMemberImages({
                  variables: {
                    imageId: idNum,
                    membershipIds: ids,
                  },
                });
              }}
              payload={{ imageId: idNum }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export { TagImage };
